import { Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { LoadErrorBanner, PAGE_COLUMN } from "@/components/ui/redesign/page";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, container } from "@/lib/utils";

interface Props {
  mode: "new" | "edit";
  title?: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  children?: React.ReactNode;
}

// The template editor (new and edit) in the workspace column: a way back to
// Templates, a clear title, then the assistant and the form as before.
export const TemplateEditorShell = ({
  mode,
  title,
  loading = false,
  error = false,
  onRetry,
  children,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <motion.div
      animate="show"
      className={cn(PAGE_COLUMN, "max-w-[880px] space-y-5 sm:space-y-6")}
      data-testid="template-editor"
      initial="hidden"
      variants={container}
    >
      <div className="space-y-2">
        <Link
          className="-ml-2 inline-flex h-11 items-center gap-1 rounded-full px-2 text-sm font-medium text-v3-text-2 hover:text-v3-text hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:h-9"
          to="/templates"
        >
          <ChevronLeft aria-hidden className="size-4" />
          {t("templates.home.editor.back")}
        </Link>
        {/* The template being edited is this page's heading (the top bar
            shows Templates). */}
        <header>
          <h1 className="text-[22px] font-bold leading-7 sm:text-[28px] sm:leading-[34px]">
            {title ||
              t(
                mode === "new"
                  ? "templates.home.editor.newTitle"
                  : "templates.home.editor.editTitle",
              )}
          </h1>
          <p className="mt-0.5 text-sm text-v3-text-3">
            {t("templates.home.editor.subtitle")}
          </p>
        </header>
      </div>
      {loading && (
        <div
          aria-busy
          aria-label={t("common.loading")}
          className="space-y-4"
          data-testid="template-editor-loading"
          role="status"
        >
          <Skeleton className="h-40 w-full rounded-xl" role="presentation" />
          <Skeleton className="h-96 w-full rounded-xl" role="presentation" />
        </div>
      )}
      {error && (
        <LoadErrorBanner
          description={t("templates.home.editor.loadError")}
          title={t("error.unknown")}
          onRetry={onRetry}
        />
      )}
      {/* Touch screens: the form's buttons (Add heading, Save, Cancel) get
          44px targets, and each heading chip's remove button a 44px hit area,
          without changing the form itself. */}
      <div className="max-lg:[&_form_button:not([role=switch],span>button)]:min-h-11 max-lg:[&_form_button:not([role=switch],span>button)]:min-w-11 max-lg:[&_form_span>button]:relative max-lg:[&_form_span>button]:after:absolute max-lg:[&_form_span>button]:after:inset-[-14px_-10px] max-lg:[&_form_span>button]:after:content-['']">
        {children}
      </div>
    </motion.div>
  );
};
